import { MenuItem, Select, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { NavLink, useHistory } from 'react-router-dom';

import { Box, ChevronDown } from 'lucide-react';

const PREFIX = 'TabsHeader';
const classes = {
  root: `${PREFIX}-root`,
  intro: `${PREFIX}-intro`,
  nav: `${PREFIX}-nav`,
  tabs: `${PREFIX}-tabs`,
  tab: `${PREFIX}-tab`,
  select: `${PREFIX}-select`,
  activeTab: `${PREFIX}-activeTab`,
  actions: `${PREFIX}-actions`,
  utils: `${PREFIX}-utils`,
};

const StyledDiv = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexDirection: 'column',

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(6),
  },
  [`& .${classes.nav}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'stretch',
    color: theme.palette.darkBlue.main,

    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  [`& .${classes.tabs}`]: {
    display: 'flex',
    justifyContent: 'start',
    flexGrow: 1,
    margin: theme.spacing(0, 0, -6, 3),
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(0, 0, -2, 3),
    },
  },
  [`& .${classes.tab}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.3,
    color: theme.palette.common.white,
    cursor: 'pointer',
    margin: theme.spacing(0, 2, 0, 0),
    padding: theme.spacing(2, 2),
    textDecoration: 'none',
    borderTop: `3px solid transparent`,
    borderBottom: `3px solid transparent`,
  },
  [`& .${classes.select}`]: {
    width: '60%',
    background: '#ffffff16',
    // padding: theme.spacing(2, 0, 0),
    borderTopLeftRadius: theme.spacing(4),
    borderTopRightRadius: theme.spacing(4),
    color: theme.palette.common.white,
  },
  [`& .${classes.activeTab}`]: {
    background: '#ffffff16',
    padding: theme.spacing(2, 4),
    borderTopLeftRadius: theme.spacing(4),
    borderTopRightRadius: theme.spacing(4),
    color: theme.palette.common.white,
  },
}));

type Tab = {
  label: React.ReactNode;
  value: string;
  icon?: React.ReactNode;
  external?: boolean;
  hide?: boolean;
};

type TabsHeaderProps = {
  tabs: Tab[];
  sx?: SxProps<Theme> | undefined;
};

const SubTabs: React.FC<TabsHeaderProps> = ({ tabs, sx }) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <StyledDiv sx={sx}>
      <div className={classes.nav}>
        <div className={classes.tabs}>
          {isMobile ? (
            <Box className={classes.select}>
              <Select
                defaultValue={tabs[0].value}
                fullWidth
                label={'Nav'}
                sx={{
                  '&.MuiInputBase-root': {
                    color: 'white !important',
                  },
                  '& .MuiSelect-icon': {
                    right: 15,
                  },
                }}
                IconComponent={(props) => (
                  <span {...props}>
                    <ChevronDown size={18} />
                  </span>
                )}
                onChange={(event) => {
                  const url = String(event.target.value);
                  if (url.includes('http')) {
                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                    if (newWindow) newWindow.opener = null;
                  } else {
                    history.push(url);
                  }
                }}>
                {tabs.map((tab) =>
                  tab.hide ? null : (
                    <MenuItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </Box>
          ) : (
            tabs.map((tab) =>
              tab.hide ? null : tab.external ? (
                <a href={tab.value} key={tab.value} className={classes.tab} target="_blank" rel="noopener noreferrer">
                  {tab.icon} {tab.label}
                </a>
              ) : tab.hide ? null : (
                <NavLink key={tab.value} to={tab.value} className={classes.tab} activeClassName={classes.activeTab}>
                  {tab.icon} {tab.label}
                </NavLink>
              )
            )
          )}
        </div>
      </div>
    </StyledDiv>
  );
};

export default React.memo(SubTabs);
